import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { useStore } from 'app/context';
import { Modal } from 'app/components/Modal';
import { Button } from 'app/components/common/Button';
import { Icon } from 'app/components/common/Icon';
import { ASP_CAMPAIGNS, aspKategorieModifier, AspZuweisung, formatAspDateShort, IAspAgtPotenzial, IAspCampaign } from './asp.mock.data';

export const agenturAspModalId = 'design.agenturAspModal';

// Häkchen: Agentur nimmt an der Kampagne teil
export const AspTeilnahmeIcon = (props: { tooltip?: string }) => (
	<span data-tooltip={props.tooltip}>
		<Icon iconClass="check-circle" />
	</span>
);

interface IAspCampaignRow {
	campaign: IAspCampaign;
	potenzial: number;
	zuweisung?: AspZuweisung;
	onToggle: (campaignId: string, zuweisung: AspZuweisung) => void;
}

const AspZuweisungButton = (props: { label: AspZuweisung; zuweisung?: AspZuweisung; onToggle: () => void }) => {
	const isActive = props.zuweisung === props.label;
	return (
		<Button className={`button ${isActive ? 'is-active' : 'is-inverted'}`} onClick={props.onToggle}>
			{props.label}
		</Button>
	);
};

const AspCampaignRow = observer((props: IAspCampaignRow) => {
	const { campaign, potenzial, zuweisung, onToggle } = props;
	return (
		<tr>
			<td>
				<div className="asp-campaign-cell">
					<span className={`asp-color-bar ${aspKategorieModifier[campaign.kategorie]}`} />
					<div>
						<strong>{campaign.name}</strong>
						<br />
						<small className="color-grey">
							{campaign.kategorie} · {campaign.impuls} · {formatAspDateShort(campaign.start)} – {formatAspDateShort(campaign.end)}
						</small>
					</div>
				</div>
			</td>
			<td className="has-text-right">
				<strong>{potenzial}</strong>
				<br />
				<small className="color-grey">Potenzial</small>
			</td>
			<td className="has-text-right">
				<div className="button-group is-radio-button-group is-small">
					<AspZuweisungButton label="Agentur" zuweisung={zuweisung} onToggle={() => onToggle(campaign.id, 'Agentur')} />
					<AspZuweisungButton label="DSE" zuweisung={zuweisung} onToggle={() => onToggle(campaign.id, 'DSE')} />
				</div>
			</td>
		</tr>
	);
});

interface IAgenturASPModal {
	vm?: IAspAgtPotenzial;
	onSave?: (assigned: Record<string, AspZuweisung>) => void;
}

export const AgenturASPModal = observer((props: IAgenturASPModal) => {
	const { uiStore } = useStore();
	const vm = props.vm;

	const [assigned, setAssigned] = useState<Record<string, AspZuweisung>>({});

	useEffect(() => {
		setAssigned(vm ? { ...vm.teilnahmen } : {});
	}, [vm]);

	if (!vm) {
		return <></>;
	}

	// erneuter Klick auf die aktive Zuweisung hebt sie wieder auf
	const toggle = (campaignId: string, zuweisung: AspZuweisung) => {
		setAssigned((prev) => {
			const next = { ...prev };
			if (next[campaignId] === zuweisung) {
				delete next[campaignId];
			} else {
				next[campaignId] = zuweisung;
			}
			return next;
		});
	};

	const onCancel = () => {
		setAssigned({ ...vm.teilnahmen });
		uiStore.hideModal(agenturAspModalId);
	};

	const onSave = () => {
		if (props.onSave) {
			props.onSave(assigned);
		}
		uiStore.hideModal(agenturAspModalId);
	};

	const topCampaigns = vm.top5.map((id) => ASP_CAMPAIGNS.find((c) => c.id === id)!).filter(Boolean);
	const otherCampaigns = ASP_CAMPAIGNS.filter((c) => !vm.top5.includes(c.id));

	return (
		<Modal
			modalId={agenturAspModalId}
			title={`Aktivitätsschwerpunkte zuweisen`}
			size="medium"
			onClose={onCancel}
			buttons={[
				{ title: 'Abbrechen', className: 'button is-secondary', onClick: onCancel },
				{ title: 'Speichern', className: 'button is-primary', onClick: onSave },
			]}
		>
			<div className="pad-bottom-1rem">
				<strong>{vm.agt.name}</strong>
				<br />
				<small className="color-grey">{vm.agt.agtId}</small>
			</div>

			<div className="pad-bottom-1rem">
				<strong>Höchstes Potenzial</strong>
				<table className="table is-fullwidth">
					<tbody>
						{topCampaigns.map((c) => (
							<AspCampaignRow key={c.id} campaign={c} potenzial={vm.potenziale[c.id]} zuweisung={assigned[c.id]} onToggle={toggle} />
						))}
					</tbody>
				</table>
			</div>

			<div className="border-top pad-top-1rem">
				<strong className="color-grey">Weitere Kampagnen</strong>
				<table className="table is-fullwidth">
					<tbody>
						{otherCampaigns.map((c) => (
							<AspCampaignRow key={c.id} campaign={c} potenzial={vm.potenziale[c.id]} zuweisung={assigned[c.id]} onToggle={toggle} />
						))}
					</tbody>
				</table>
			</div>

			<div className="note-box is-turquoise bg-turquoise">
				<Icon iconClass="info-circle" />
				<span className="asp-note-text">
					Das Potenzial zeigt die Anzahl der Kunden mit identifiziertem Interesse an der Kampagne. Zugewiesene Kampagnen erscheinen im Agentur-Dashboard und im JPG.
				</span>
			</div>
		</Modal>
	);
});

// kleine Wiederverwendung für Zellen/Widgets: Top-Potenziale fett, Teilnahme-Häkchen nur bei Zuweisung durch die Agentur
export const AspPotenzialZelle = observer((props: { potenzial: number; isTop: boolean; zuweisung?: AspZuweisung }) => {
	const { potenzial, isTop, zuweisung } = props;
	if (zuweisung === 'Agentur') {
		return (
			<span className="asp-inline">
				<strong>{potenzial}</strong>
				<AspTeilnahmeIcon tooltip={`Nimmt bereits teil (${zuweisung})`} />
			</span>
		);
	}
	return <span className="asp-inline">{isTop ? <strong>{potenzial}</strong> : potenzial}</span>;
});
